import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';

import { healthApi } from 'Api/health.api';

import { LockIcon, VisibilityIcon, VisibilityOffIcon } from '@libs/shared/ui/icons';

interface AdminLoginModalProps {
  open: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export const AdminLoginModal: React.FC<AdminLoginModalProps> = ({ open, onSuccess, onCancel }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await healthApi.verifyAdminPassword(password);

      if (result.isSuccess && result.data?.isValid) {
        // Store authentication in session storage for this browser session
        sessionStorage.setItem('adminAuthenticated', 'true');
        sessionStorage.setItem('adminAuthTime', Date.now().toString());
        onSuccess();
      } else {
        setError('Invalid password. Please try again.');
      }
    } catch (err) {
      console.error('Password verification error:', err);
      setError('Failed to verify password. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setPassword('');
    setError(null);
    onCancel();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <LockIcon color="primary" />
          <Typography variant="h6" component="span">
            Admin Access Required
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Enter the admin password to access system health information.
          </Typography>

          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Admin Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={isLoading}
            error={!!error}
            helperText={error}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end" disabled={isLoading}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button onClick={handleCancel} disabled={isLoading} color="inherit">
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !password.trim()}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isLoading ? 'Verifying...' : 'Access'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
